import { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button, Input, FormGroup, Label } from '../../components/shared/StyledComponents'

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
`

const LoginCard = styled.div`
  background: white;
  padding: ${({ theme }) => theme.spacing.xxl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 100%;
  max-width: 400px;
`

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const LogoIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`

const Subtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.danger}15;
  color: ${({ theme }) => theme.colors.danger};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

/** Set by a page that sends the person to log in again (Imputación) to bring them back afterwards. */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

/**
 * Where to go after a successful login: the stored internal path, once, or '/'.
 * Only a same-origin path is followed ('/x', never '//host' or '/\host', which
 * browsers read as another site). Whitespace and control characters are refused
 * too: URL parsing strips tabs and newlines, so '/\t/host' would become '//host'.
 */
export function consumePostLoginRedirect(): string {
    try {
        const target = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
        if (target === null) return '/'
        window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
        // eslint-disable-next-line no-control-regex
        const unsafe = /[\s\u0000-\u001f\u007f]/.test(target)
        if (!unsafe && target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/\\')) return target
    } catch {
        // Storage unavailable: default landing page.
    }
    return '/'
}

export const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const { login } = useAuth()
    const navigate = useNavigate()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setIsLoading(true)

        const success = await login(username, password)

        if (success) {
            navigate(consumePostLoginRedirect())
        } else {
            setError('Credenciales inválidas')
        }

        setIsLoading(false)
    }

    return (
        <LoginContainer>
            <LoginCard className="animate-fade-in">
                <Logo>
                    <LogoIcon />
                    <Title>Manager InputKnowledge</Title>
                </Logo>
                <Subtitle>Inicia sesión para continuar</Subtitle>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="username">Usuario</Label>
                        <Input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Tu nombre de usuario"
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="password">Contraseña</Label>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Tu contraseña"
                            required
                        />
                    </FormGroup>

                    <Button type="submit" fullWidth disabled={isLoading}>
                        {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                    </Button>
                </form>
            </LoginCard>
        </LoginContainer>
    )
}
